import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getUser,
  updateUser,
  updateCharacter,
  deleteCharacter,
  startRegeneration,
  startTeamImageGeneration,
  getJobStatus,
  UserDetailResponse,
  CharacterResponse,
} from '../../api/admin';
import AvatarPreview from '../../components/AvatarPreview';
import './Admin.css';

const UserDetail: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Edit states
  const [editCoins, setEditCoins] = useState<number | null>(null);
  const [editAdmin, setEditAdmin] = useState<boolean | null>(null);
  const [editGrade, setEditGrade] = useState<number | null>(null);
  const [editingChar, setEditingChar] = useState<string | null>(null);
  const [charXp, setCharXp] = useState(0);
  const [charLevel, setCharLevel] = useState(1);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [regenerateConfirm, setRegenerateConfirm] = useState<string | null>(null);
  const [regenerating, setRegenerating] = useState<string | null>(null);
  const [regenSuccess, setRegenSuccess] = useState<string | null>(null);
  const [generatingTeamImage, setGeneratingTeamImage] = useState(false);
  const [teamImages, setTeamImages] = useState<{ lineup: string; action: string } | null>(null);

  const fetchUser = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getUser(userId);
      setUser(data);
    } catch (err) {
      setError('Failed to load user');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const handleSaveUser = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const updates: { coins?: number; is_admin?: boolean; grade?: number } = {};
      if (editCoins !== null && editCoins !== user.coins) {
        updates.coins = editCoins;
      }
      if (editAdmin !== null && editAdmin !== user.is_admin) {
        updates.is_admin = editAdmin;
      }
      if (editGrade !== null && editGrade !== user.grade) {
        updates.grade = editGrade;
      }
      if (Object.keys(updates).length > 0) {
        const updated = await updateUser(user.id, updates);
        setUser(updated);
      }
      setEditCoins(null);
      setEditAdmin(null);
      setEditGrade(null);
    } catch (err) {
      setError('Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleEditChar = (char: CharacterResponse) => {
    setEditingChar(char.id);
    setCharXp(char.xp);
    setCharLevel(char.level);
  };

  const handleSaveChar = async () => {
    if (!user || editingChar === null) return;
    setSaving(true);
    try {
      await updateCharacter(user.id, editingChar, {
        xp: charXp,
        level: charLevel,
      });
      await fetchUser();
      setEditingChar(null);
    } catch (err) {
      setError('Failed to save character');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteChar = async (charId: string) => {
    if (!user) return;
    setSaving(true);
    try {
      await deleteCharacter(user.id, charId);
      await fetchUser();
      setDeleteConfirm(null);
    } catch (err) {
      setError('Failed to delete character');
    } finally {
      setSaving(false);
    }
  };

  const pollJobUntilDone = async (jobId: string): Promise<{ status: string; stepsCount?: number; error?: string }> => {
    const MAX_POLLS = 60;
    const POLL_INTERVAL = 3000;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
      const job = await getJobStatus(jobId);
      if (job.status === 'completed') {
        return { status: 'completed', stepsCount: job.result?.steps_count };
      }
      if (job.status === 'failed') {
        return { status: 'failed', error: job.error || 'Unknown error' };
      }
    }
    return { status: 'timeout' };
  };

  const handleRegenerate = async (charId: string) => {
    setRegenerating(charId);
    setRegenerateConfirm(null);
    setError(null);
    setRegenSuccess(null);
    try {
      const startResult = await startRegeneration(charId);
      const pollResult = await pollJobUntilDone(startResult.job_id);

      if (pollResult.status === 'completed') {
        setRegenSuccess(
          `Regenerated avatar for ${startResult.character_name} (${pollResult.stepsCount ?? '?'} steps)`
        );
        await fetchUser();
      } else if (pollResult.status === 'failed') {
        setError(`Regeneration failed: ${pollResult.error}`);
      } else {
        setRegenSuccess('Regeneration is still running. Refresh in a minute to see the result.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start regeneration');
    } finally {
      setRegenerating(null);
    }
  };

  const handleGenerateTeamImage = async () => {
    if (!userId) return;
    setGeneratingTeamImage(true);
    setError(null);
    setTeamImages(null);
    try {
      const startResult = await startTeamImageGeneration(userId);
      const pollResult = await pollJobUntilDone(startResult.job_id);

      if (pollResult.status === 'completed') {
        const job = await getJobStatus(startResult.job_id);
        if (job.result) {
          const result = job.result as unknown as {
            lineup_image_url: string;
            action_image_url: string;
          };
          setTeamImages({
            lineup: result.lineup_image_url,
            action: result.action_image_url,
          });
        }
      } else if (pollResult.status === 'failed') {
        setError(`Team image generation failed: ${pollResult.error}`);
      } else {
        setError('Team image generation timed out. Try again later.');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to start team image generation');
    } finally {
      setGeneratingTeamImage(false);
    }
  };

  if (loading) {
    return <div className="admin-empty"><p>Loading...</p></div>;
  }

  if (!user) {
    return <div className="admin-empty"><p>User not found</p></div>;
  }

  const isEditing = editCoins !== null || editAdmin !== null || editGrade !== null;

  const formatGrade = (grade: number | null): string => {
    if (grade === null) return 'Not set';
    if (grade === 0) return 'K';
    return `Grade ${grade}`;
  };

  return (
    <div>
      <div className="admin-page-header">
        <h2>User: {user.username}</h2>
        <button
          className="admin-btn admin-btn-secondary"
          onClick={() => navigate('/admin/users')}
        >
          Back to Users
        </button>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {regenSuccess && <div className="admin-alert admin-alert-success">{regenSuccess}</div>}

      <div className="user-detail-header">
        <div className="user-info-card">
          <h3>User Information</h3>
          <div className="user-info-row">
            <span className="user-info-label">Username</span>
            <span className="user-info-value">{user.username}</span>
          </div>
          <div className="user-info-row">
            <span className="user-info-label">Coins</span>
            <span className="user-info-value">
              {editCoins !== null ? (
                <input
                  type="number"
                  className="admin-inline-input"
                  value={editCoins}
                  onChange={(e) => setEditCoins(parseInt(e.target.value) || 0)}
                  min={0}
                  max={999999}
                />
              ) : (
                <>
                  {user.coins}
                  <button
                    className="admin-btn admin-btn-small admin-btn-secondary"
                    style={{ marginLeft: '10px' }}
                    onClick={() => setEditCoins(user.coins)}
                  >
                    Edit
                  </button>
                </>
              )}
            </span>
          </div>
          <div className="user-info-row">
            <span className="user-info-label">Grade</span>
            <span className="user-info-value">
              {editGrade !== null ? (
                <select
                  className="admin-inline-input"
                  value={editGrade}
                  onChange={(e) => setEditGrade(parseInt(e.target.value))}
                >
                  <option value={0}>K</option>
                  <option value={1}>1</option>
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                </select>
              ) : (
                <>
                  {formatGrade(user.grade)}
                  <button
                    className="admin-btn admin-btn-small admin-btn-secondary"
                    style={{ marginLeft: '10px' }}
                    onClick={() => setEditGrade(user.grade ?? 0)}
                  >
                    Edit
                  </button>
                </>
              )}
            </span>
          </div>
          <div className="user-info-row">
            <span className="user-info-label">Admin</span>
            <span className="user-info-value">
              <label className="admin-toggle">
                <input
                  type="checkbox"
                  checked={editAdmin !== null ? editAdmin : user.is_admin}
                  onChange={(e) => setEditAdmin(e.target.checked)}
                />
                <span className="admin-toggle-slider"></span>
              </label>
            </span>
          </div>
          <div className="user-info-row">
            <span className="user-info-label">Created</span>
            <span className="user-info-value">
              {new Date(user.created_at).toLocaleString()}
            </span>
          </div>
          {isEditing && (
            <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSaveUser}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => {
                  setEditCoins(null);
                  setEditAdmin(null);
                  setEditGrade(null);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="admin-card">
        <h3>Characters ({user.characters.length})</h3>
        {user.characters.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)' }}>No characters</p>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Avatar</th>
                <th>Name</th>
                <th>Level</th>
                <th>XP</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {user.characters.map((char) => (
                <tr key={char.id}>
                  <td>
                    {char.avatar_url && (
                      <AvatarPreview src={char.avatar_url} alt={char.name} />
                    )}
                  </td>
                  <td>{char.name}</td>
                  <td>
                    {editingChar === char.id ? (
                      <input
                        type="number"
                        className="admin-inline-input"
                        value={charLevel}
                        onChange={(e) => setCharLevel(parseInt(e.target.value) || 1)}
                        min={1}
                        max={4}
                      />
                    ) : (
                      char.level
                    )}
                  </td>
                  <td>
                    {editingChar === char.id ? (
                      <input
                        type="number"
                        className="admin-inline-input"
                        value={charXp}
                        onChange={(e) => setCharXp(parseInt(e.target.value) || 0)}
                        min={0}
                        max={999999}
                      />
                    ) : (
                      char.xp
                    )}
                  </td>
                  <td>{new Date(char.created_at).toLocaleDateString()}</td>
                  <td>
                    <div className="admin-btn-group">
                      {editingChar === char.id ? (
                        <>
                          <button
                            className="admin-btn admin-btn-primary admin-btn-small"
                            onClick={handleSaveChar}
                            disabled={saving}
                          >
                            Save
                          </button>
                          <button
                            className="admin-btn admin-btn-secondary admin-btn-small"
                            onClick={() => setEditingChar(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            className="admin-btn admin-btn-secondary admin-btn-small"
                            onClick={() => handleEditChar(char)}
                          >
                            Edit
                          </button>
                          <button
                            className="admin-btn admin-btn-secondary admin-btn-small"
                            onClick={() => setRegenerateConfirm(char.id)}
                            disabled={regenerating === char.id || char.level < 2}
                            title={char.level < 2 ? 'No level-ups to regenerate' : 'Regenerate avatar'}
                          >
                            {regenerating === char.id ? 'Regenerating...' : 'Regen'}
                          </button>
                          <button
                            className="admin-btn admin-btn-danger admin-btn-small"
                            onClick={() => setDeleteConfirm(char.id)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Team Image Generation */}
      <div className="admin-card">
        <h3>Team Image</h3>
        <button
          className="admin-btn admin-btn-primary"
          onClick={handleGenerateTeamImage}
          disabled={generatingTeamImage || user.characters.length === 0}
          title={user.characters.length === 0 ? 'No characters to generate team image' : 'Generate team image'}
        >
          {generatingTeamImage ? 'Generating...' : 'Generate Team Image'}
        </button>
        {teamImages && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ marginBottom: '20px' }}>
              <h4>Lineup</h4>
              <img
                src={teamImages.lineup}
                alt="Team Lineup"
                style={{ width: '100%', maxWidth: '800px', border: '1px solid var(--border-color)', borderRadius: '8px' }}
              />
            </div>
            <div>
              <h4>Action</h4>
              <img
                src={teamImages.action}
                alt="Team Action"
                style={{ width: '100%', maxWidth: '800px', border: '1px solid var(--border-color)', borderRadius: '8px' }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm !== null && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Delete Character</h3>
            <p className="confirm-dialog-text">
              Are you sure you want to delete this character? This action cannot be undone.
            </p>
            <div className="admin-modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-danger"
                onClick={() => handleDeleteChar(deleteConfirm)}
                disabled={saving}
              >
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Regenerate Confirmation Modal */}
      {regenerateConfirm !== null && (
        <div className="admin-modal-overlay">
          <div className="admin-modal">
            <h3>Regenerate Avatar</h3>
            <p className="confirm-dialog-text">
              Regenerate avatar for <strong>{user.characters.find(c => c.id === regenerateConfirm)?.name}</strong>?
              This will re-run all enhancement generations using current prompts.
              This may take up to 90 seconds.
            </p>
            <div className="admin-modal-actions">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setRegenerateConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={() => handleRegenerate(regenerateConfirm)}
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDetail;
